import { Reducer } from "react";

export interface FileUploadStateItem<A> {
  file?: File;
  attachment?: A;
  progress?: number;
  error?: string;
}

export interface FileUploadState<A> {
  items: Array<FileUploadStateItem<A>>;
}

export type FileUploadAction<A> =
  | { type: "ADD_FILES"; files: File[] }
  | { type: "ADD_ATTACHMENTS"; attachments: A[] }
  | { type: "UPDATE_ITEM_BY_FILE"; file: File; update: (item: FileUploadStateItem<A>) => FileUploadStateItem<A> }
  | {
      type: "UPDATE_ITEM_BY_ATTACHMENT";
      item: FileUploadStateItem<A>;
      update: (item: FileUploadStateItem<A>) => FileUploadStateItem<A>;
    }
  | { type: "SET_DEFAULT_CV"; item: FileUploadStateItem<A> }
  | { type: "REMOVE_ITEM"; item: FileUploadStateItem<A> }
  | { type: "RESET_PROGRESS" };

export type FileUploadReducer<A> = Reducer<FileUploadState<A>, FileUploadAction<A>>;

export const getFileUploadInitialState = <A>(): FileUploadState<A> => {
  return {
    items: [],
  };
};

export const getFileUploadReducer = <A>(): FileUploadReducer<A> => {
  return (state, action) => {
    switch (action.type) {
      case "ADD_FILES": {
        const newItems = action.files.map<FileUploadStateItem<A>>(f => ({
          file: f,
          progress: 0,
        }));
        return { ...state, items: [...state.items, ...newItems] };
      }

      case "ADD_ATTACHMENTS": {
        const newItems = action.attachments.map<FileUploadStateItem<A>>(a => ({
          attachment: a,
        }));
        return { ...state, items: [...state.items, ...newItems] };
      }

      case "UPDATE_ITEM_BY_FILE": {
        const item = state.items.find(i => i.file === action.file);
        if (!item) {
          return { ...state };
        }

        const itemIndex = state.items.indexOf(item);
        const newItem = action.update(item);
        const newItems = [...state.items];
        newItems[itemIndex] = newItem;

        return { ...state, items: newItems };
      }

      case "UPDATE_ITEM_BY_ATTACHMENT": {
        const item = state.items.find(i => i === action.item) || state.items.find(i => i.attachment === action.item);

        if (!item) {
          return { ...state };
        }

        const itemIndex = state.items.indexOf(item);
        const newItem = action.update(item);
        const newItems = [...state.items];
        newItems[itemIndex] = newItem;

        return { ...state, items: newItems };
      }

      case "SET_DEFAULT_CV": {
        const item = state.items.find(i => i === action.item) || state.items.find(i => i.attachment === action.item);

        if (!item) {
          return { ...state };
        }

        const itemIndex = state.items.indexOf(item);

        const newItems = state.items.map<FileUploadStateItem<A>>((item, index) => ({
          ...item,
          attachment: {
            ...item.attachment!,
            defaultFile: index === itemIndex,
          },
        }));

        return { ...state, items: newItems };
      }

      case "REMOVE_ITEM": {
        return { ...state, items: state.items.filter(i => i !== action.item) };
      }

      case "RESET_PROGRESS": {
        return {
          ...state,
          items: state.items.map(item => ({
            ...item,
            progress: undefined,
          })),
        };
      }
    }
  };
};
